import { showToast } from './Toast.js';

type NavigateFn = (route: { name: string; id?: string } & Record<string, unknown>) => void;

export function renderNewSession(container: HTMLElement, navigate: NavigateFn): void {
  container.innerHTML = `
    <div class="page-header">
      <h1 class="page-title"><i class="bi bi-stars"></i> AI Session</h1>
      <p class="page-subtitle">Generate AI content and create a legally provable record of exactly what was AI-written vs. human-edited</p>
    </div>

    <!-- How it works strip -->
    <div class="session-how-strip">
      <div class="session-how-step">
        <div class="session-how-num">1</div>
        <div class="session-how-body">
          <div class="session-how-title">Write your prompt</div>
          <div class="session-how-desc">Describe what you need — a menu description, contract clause, insurance summary, etc.</div>
        </div>
      </div>
      <div class="session-how-arrow"><i class="bi bi-arrow-right"></i></div>
      <div class="session-how-step">
        <div class="session-how-num">2</div>
        <div class="session-how-body">
          <div class="session-how-title">Review &amp; edit the AI output</div>
          <div class="session-how-desc">Every change you make is tracked. PREUVU records exactly what the AI wrote and exactly what you changed.</div>
        </div>
      </div>
      <div class="session-how-arrow"><i class="bi bi-arrow-right"></i></div>
      <div class="session-how-step">
        <div class="session-how-num">3</div>
        <div class="session-how-body">
          <div class="session-how-title">Certify the document</div>
          <div class="session-how-desc">Creates a tamper-evident certificate with a unique ID. Verifiable by anyone at any time — in court, by a regulator, or by a client.</div>
        </div>
      </div>
    </div>

    <!-- Why this matters callout -->
    <div class="session-callout">
      <i class="bi bi-shield-check session-callout-icon"></i>
      <div>
        <strong>Why it matters:</strong>
        Laws like the EU AI Act and Washington State SB 5838 require businesses to disclose when content was AI-generated.
        This session creates the audit trail that proves you complied — timestamped, cryptographically signed, and immutable.
      </div>
    </div>

    <div class="card">
      <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px;margin-bottom:18px">
        <i class="bi bi-pencil-square" style="color:var(--brand-gold)"></i> Step 1 — Set Up Your Session
      </div>
      <form id="session-form">
        <div class="form-row">
          <label>Document Title <span style="color:var(--danger)">*</span></label>
          <input type="text" id="doc-title"
            placeholder="e.g. &quot;Summer Menu Descriptions — June 2026&quot; or &quot;Contract Clause #4 Review&quot;"
            required />
          <div class="field-hint">This becomes the title of your certificate. Be specific — it appears on the signed PDF.</div>
        </div>

        <div class="form-row-2">
          <div>
            <label>AI Provider</label>
            <select id="ai-provider">
              <option value="huggingface">HuggingFace (Free tier)</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
            </select>
          </div>
          <div>
            <label>Model</label>
            <select id="ai-model">
              <option value="mistral-7b">Mistral 7B Instruct (free)</option>
              <option value="qwen-2.5-7b">Qwen 2.5 7B (free)</option>
              <option value="phi-3-mini">Phi-3 Mini (free)</option>
              <option value="llama-3-8b">Llama 3 8B (free)</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <label>Role / Context <span style="font-size:11px;color:var(--text-muted);font-weight:400">(optional — tells the AI what role to play)</span></label>
          <textarea id="system-prompt" style="min-height:60px"
            placeholder="e.g. &quot;You are a professional menu copywriter for upscale restaurants. Keep descriptions under 40 words.&quot;"></textarea>
        </div>

        <div class="form-row">
          <label>Your Prompt <span style="color:var(--danger)">*</span></label>
          <textarea id="user-prompt" style="min-height:160px"
            placeholder="Describe exactly what you want the AI to write. This is logged word-for-word as part of the compliance record.

Examples:
• &quot;Write a 35-word description for our pan-seared salmon with lemon butter sauce and asparagus.&quot;
• &quot;Summarize the key risk factors in the attached insurance claim for policy #INS-4821.&quot;
• &quot;Draft a confidentiality clause for a freelance contractor agreement under Washington State law.&quot;"
            required></textarea>
          <div class="field-hint"><i class="bi bi-lock-fill" style="color:var(--brand-gold)"></i> This prompt is hashed and sealed the moment you send it — it cannot be altered after the fact.</div>
        </div>

        <details class="session-advanced">
          <summary><i class="bi bi-sliders"></i> Advanced options</summary>
          <div class="form-row-2" style="margin-top:14px">
            <div>
              <label>Max Tokens</label>
              <input type="number" id="max-tokens" value="2048" min="100" max="8192" />
              <div class="field-hint">Controls response length. 2048 ≈ ~1,500 words.</div>
            </div>
            <div>
              <label>Temperature</label>
              <input type="number" id="temperature" value="0.7" min="0" max="2" step="0.1" />
              <div class="field-hint">0 = precise/factual, 1+ = more creative.</div>
            </div>
          </div>
        </details>

        <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px">
          <button type="button" class="btn btn-ghost" id="cancel-btn">Cancel</button>
          <button type="submit" class="btn btn-gold btn-lg" id="submit-btn">
            <span id="submit-icon"><i class="bi bi-send"></i></span>
            <span id="submit-text">Send to AI &amp; Start Logging</span>
          </button>
        </div>
      </form>
    </div>

    <div id="response-panel" style="display:none">
      <div class="card" style="margin-top:24px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
          <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">
            <i class="bi bi-pencil" style="color:var(--brand-gold)"></i> Step 2 — Review &amp; Edit
          </div>
          <div style="display:flex;gap:8px;align-items:center">
            <span class="badge badge-draft" id="session-status-badge">draft</span>
            <span style="font-size:11px;color:var(--text-muted)" id="session-id-display"></span>
          </div>
        </div>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">
          Make any changes you need below. Every edit is tracked automatically — the certificate will show exactly what percentage a human changed vs. what the AI originally wrote.
        </p>

        <div class="form-row">
          <div class="editor-label-row">
            <label>AI Output — edit freely</label>
            <span class="editor-diff-badge" id="diff-badge">0% human edits</span>
          </div>
          <textarea id="final-output" style="min-height:260px;user-select:text"></textarea>
        </div>

        <div class="form-row">
          <label style="display:flex;align-items:center;gap:6px">
            <i class="bi bi-fingerprint" style="color:var(--brand-gold)"></i> Content Fingerprint
            <span style="font-size:11px;color:var(--text-muted);font-weight:400">— changes if anyone alters the document after certification</span>
          </label>
          <div class="hash-display" id="watermark-hash">—</div>
        </div>

        <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:8px" id="action-buttons">
          <button class="btn btn-ghost" id="export-btn" style="display:none"><i class="bi bi-download"></i> Export Audit JSON</button>
          <button class="btn btn-primary" id="certify-btn">
            <i class="bi bi-patch-check"></i> Certify &amp; Lock Document
          </button>
        </div>

        <div id="cert-panel" style="display:none;margin-top:20px">
          <div class="verify-result valid">
            <div style="font-size:15px;font-weight:700;color:var(--success);margin-bottom:4px">
              <i class="bi bi-patch-check-fill"></i> Document Certified
            </div>
            <div style="font-size:12px;color:var(--text-muted);margin-bottom:4px" id="cert-id-display"></div>
            <p style="font-size:12px;color:var(--text-secondary);margin-bottom:14px;line-height:1.6">
              This document is now sealed. The certificate proves who generated it, which AI model was used,
              what the original AI output was, what percentage a human edited, and the exact timestamp —
              all cryptographically signed and impossible to forge.
            </p>
            <div style="display:flex;gap:10px;flex-wrap:wrap">
              <button class="btn btn-ghost btn-sm" id="view-cert-btn"><i class="bi bi-eye"></i> View Full Audit Trail</button>
              <button class="btn btn-primary btn-sm" id="generate-pdf-btn"><i class="bi bi-file-earmark-pdf"></i> Download PDF Certificate</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  document.getElementById('cancel-btn')!.addEventListener('click', () => navigate({ name: 'dashboard' }));

  const MODEL_OPTIONS: Record<string, string> = {
    huggingface: `<option value="mistral-7b">Mistral 7B Instruct (free)</option>
                  <option value="qwen-2.5-7b">Qwen 2.5 7B (free)</option>
                  <option value="phi-3-mini">Phi-3 Mini (free)</option>
                  <option value="llama-3-8b">Llama 3 8B (free)</option>`,
    anthropic: `<option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                <option value="claude-opus-4-7">claude-opus-4-7</option>
                <option value="claude-haiku-4-5-20251001">claude-haiku-4-5</option>`,
    openai: `<option value="gpt-4o">gpt-4o</option>
             <option value="gpt-4o-mini">gpt-4o-mini</option>`,
  };

  document.getElementById('ai-provider')!.addEventListener('change', (e) => {
    const provider = (e.target as HTMLSelectElement).value;
    const modelSelect = document.getElementById('ai-model') as HTMLSelectElement;
    modelSelect.innerHTML = MODEL_OPTIONS[provider] ?? MODEL_OPTIONS['huggingface']!;
  });

  let currentSessionId: string | null = null;

  const form = document.getElementById('session-form') as HTMLFormElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await handleSubmit();
  });

  async function handleSubmit(): Promise<void> {
    const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement;
    const submitIcon = document.getElementById('submit-icon')!;
    const submitText = document.getElementById('submit-text')!;

    submitBtn.disabled = true;
    submitIcon.innerHTML = '<span class="spinner"></span>';
    submitText.textContent = 'Sending to AI…';

    try {
      const params = {
        prompt: (document.getElementById('user-prompt') as HTMLTextAreaElement).value,
        systemPrompt: (document.getElementById('system-prompt') as HTMLTextAreaElement).value || undefined,
        provider: (document.getElementById('ai-provider') as HTMLSelectElement).value,
        model: (document.getElementById('ai-model') as HTMLSelectElement).value,
        documentTitle: (document.getElementById('doc-title') as HTMLInputElement).value,
        maxTokens: parseInt((document.getElementById('max-tokens') as HTMLInputElement).value),
        temperature: parseFloat((document.getElementById('temperature') as HTMLInputElement).value),
      };

      const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
      const result = await preuve.sendToAI(params) as { session: { id: string; watermarkHash: string }; response: { content: string } };

      currentSessionId = result.session.id;

      (document.getElementById('final-output') as HTMLTextAreaElement).value = result.response.content;
      document.getElementById('watermark-hash')!.textContent = result.session.watermarkHash;
      document.getElementById('session-id-display')!.textContent = `ID: ${result.session.id.substring(0, 8)}…`;
      document.getElementById('response-panel')!.style.display = 'block';
      document.getElementById('export-btn')!.style.display = '';

      setupEditTracking(currentSessionId);
      showToast('AI response received and logged', 'success');
    } catch (err) {
      showToast(`Error: ${(err as Error).message}`, 'error');
    } finally {
      submitBtn.disabled = false;
      submitIcon.textContent = '✦';
      submitText.textContent = 'Send & Log Session';
    }
  }

  function setupEditTracking(sessionId: string): void {
    const textarea = document.getElementById('final-output') as HTMLTextAreaElement;
    const diffBadge = document.getElementById('diff-badge')!;
    let debounceTimer: ReturnType<typeof setTimeout>;

    textarea.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        try {
          const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
          const diff = await preuve.recordHumanEdit(sessionId, textarea.value) as { humanEditPercentage: number };
          const pct = diff.humanEditPercentage;
          diffBadge.textContent = `${pct}% human edits`;
          diffBadge.className = `editor-diff-badge ${pct > 0 ? 'changed' : ''}`;
        } catch {
          // Preview mode — skip
        }
      }, 800);
    });

    document.getElementById('certify-btn')!.addEventListener('click', async () => {
      const certifyBtn = document.getElementById('certify-btn') as HTMLButtonElement;
      certifyBtn.disabled = true;
      certifyBtn.innerHTML = '<span class="spinner"></span> Certifying…';

      try {
        const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
        const cert = await preuve.certifyDocument(sessionId) as { certId: string };

        document.getElementById('session-status-badge')!.className = 'badge badge-certified';
        document.getElementById('session-status-badge')!.textContent = 'certified';
        document.getElementById('cert-id-display')!.textContent = `Certificate ID: ${cert.certId}`;
        document.getElementById('cert-panel')!.style.display = 'block';
        certifyBtn.style.display = 'none';

        document.getElementById('view-cert-btn')!.addEventListener('click', () => {
          navigate({ name: 'session', id: sessionId });
        });

        document.getElementById('generate-pdf-btn')!.addEventListener('click', async () => {
          const pdfPath = await preuve.generatePDF(sessionId) as string;
          showToast(`PDF saved: ${pdfPath}`, 'success');
        });

        showToast('Document certified successfully', 'success');
      } catch (err) {
        showToast(`Certification failed: ${(err as Error).message}`, 'error');
        certifyBtn.disabled = false;
        certifyBtn.textContent = '◈ Certify Document';
      }
    });

    document.getElementById('export-btn')!.addEventListener('click', async () => {
      try {
        const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
        const report = await preuve.exportReport(sessionId) as string;
        const blob = new Blob([report], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `preuve-audit-${sessionId.substring(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('Audit report exported', 'success');
      } catch (err) {
        showToast(`Export failed: ${(err as Error).message}`, 'error');
      }
    });
  }
}
